/* eslint-disable react/jsx-no-bind */
/* eslint-disable camelcase */
import React, { useState, useCallback, useRef } from 'react';
import SimpleBar from 'simplebar-react';
import 'simplebar-react/dist/simplebar.min.css';
import { useSnapshot } from 'valtio';

import { refsStore, videoStore } from '../../managers/globalState';

import Overlay from '../overlays';
import GridList from '../grid_list';
import Filter from '../filter';

import LayerGroupSolidSVG from '../../../www/assets/images/layers.svg';
import ArrowUpDown from '../../../www/assets/images/arrow-nav-filled.svg';

import useDimensions from '../../hooks/useDimensions';

import '../../../www/assets/css/overlays/vitals.css';

import {
	VITAL_CATEGORIES,
	VITALS_CATEGORY_INDEX
} from '../../config/constants';

/**
 * Constant vars
 */
const THUMB_WIDTH = 343;
const THUMB_HEIGHT = 337;
const TITLE_TEXT = 'العلامات الحيوية';
const Category_AR = [VITAL_CATEGORIES[0][1], VITAL_CATEGORIES[1][1]];


// TODO: While this is a valid way to declare the nested vital row object,
// it is prefered to keep our method of declaration consistent with other components for example footer.js
const NestedVitalRow = props => {
	const { nested, title, onItemClick, isMobile, overlayData, scrollableRef } = props;
	const [open, setOpen] = useState(false);
	// click handler
	const onClick = useCallback((value, group) => e => {
		onItemClick(value, group);
	}, [onItemClick]);

	return (
		<div className='vital-mobile-row flex-grid-item fadein' style={{ padding: '15px' }}>
			<h6
				className='vital-title'
				onClick={e => {
					setOpen(!open);
					onClick();
				}}
			>
				{title}
				<ArrowUpDown className={open ? 'vital-arrow-up' : 'vital-arrow-down'} />
			</h6>
			{open && (
				<GridList
					rowClassName='vital-mobile-row nested'
					maxPerRow={1}
					dataList={overlayData}
					itemPadding={[10, 10]}
					itemComponent={<VitalItem isOpen={open} nested={nested} isMobile={isMobile} onItemClick={onItemClick} />}
					scrollableRef={scrollableRef}
					nested
				/>
			)}
		</div>
	);
};

/**
 * Individual VitalItem component to represent each vital in the grid
 */
const VitalItem = props => {
	const { isOpen, nested, sphereMapThumbSrc, title, value, description, shortDescription, datasetGroups, onItemClick, isMobile } = props;

	const [open, setOpen] = useState(isOpen);

	// get dimensions hook
	const [ref] = useDimensions(true);

	// click handler
	const onClick = useCallback((value, group) => e => {
		onItemClick(value, group);
	}, [onItemClick]);

	// calculate vars
	const imgSrcUrl = `assets/images/graphics/${sphereMapThumbSrc}`;
	const imgClasses = value === 'satellites' ? 'thumbnail' : 'thumbnail-dataset';
	const topContent = () => {
		if (datasetGroups?.length > 0) {
			// \u00a0 is a jsx friendly &nbsp;
			return (
				<span
					className='vital-dataset-title'
					onClick={onClick(value, 0)}
				>
					{`${datasetGroups[0].title}\u00a0›`}
				</span>
			);
		}
		return null;
	};

	// create dataset content if we having groupings
	const datasetContent = () => {
		if (datasetGroups?.length > 1) {
			return (
				<div className='vital-datasets'>
					{
						datasetGroups.map((group, i) => {
							if (i === 0) {
								return null;
							}
							// \u00a0 is a jsx friendly &nbsp;
							return (
								<span
									key={`key-${group.id}`}
									className='vital-dataset-subtitle'
									onClick={onClick(value, i)}
								>
									{`${group.title}\u00a0›`}
								</span>
							);
						})
					}
				</div>
			);
		}
		return null;
	};

	const descriptionText = shortDescription || description || '';

	if (isMobile) {
		return (
			<div>
				{
					!nested && (
						<h6
							className='vital-title'
							onClick={() => {
								setOpen(!open);
								onClick(value);
							}}
						>
							{title}
							{datasetGroups?.length > 0 && <ArrowUpDown className={open ? 'vital-arrow-up' : 'vital-arrow-down'} />}
						</h6>
					)
				}

				{
					open && (
						<div style={{ padding: '10px 0px 10px 20px' }}>
							<div className='row-open'>
								<div>
									{topContent()}
									<p ref={ref} className='vital-description'>{descriptionText}</p>
								</div>
								<img
									className={imgClasses}
									width={`${THUMB_WIDTH}px`}
									height={`${THUMB_HEIGHT}px`}
									src={imgSrcUrl}
									alt={title}
									onClick={onClick(value)}
								/>
							</div>
							{datasetContent()}
						</div>
					)
				}
			</div>
		);
	}

	return (
		<div>
			<div className='vital-thumbnail-container'>
				<img
					className={imgClasses}
					width={`${THUMB_WIDTH}px`}
					height={`${THUMB_HEIGHT}px`}
					src={imgSrcUrl}
					alt={title}
					onClick={onClick(value)}
				/>
			</div>
			<h6
				className='vital-title hover'
				onClick={onClick(value)}
			>
				{title}

			</h6>
			<div className='vital-description-container'>
				{topContent()}
				<p ref={ref} className='vital-description'>{descriptionText}</p>
			</div>
			{datasetContent()}
		</div>
	);
};

/**
 * Vitals Overlay component
 */
const VitalsOverlay = ({ data: { allVitals, allVideos }, closeOverlay, isMobile, isWindows }) => {
	const mainRef = useRef();
	const scrollableNodeRef = React.createRef();

	// get the get ref.
	const { get } = useSnapshot(refsStore.state);

	// category state and update
	const [categoryIndex, setCategoryIndex] = useState(VITALS_CATEGORY_INDEX);
	const updateCategory = useCallback(e => {
		const newCategory = e.currentTarget.getAttribute('data-filter');

		const newCategoryIndex = Category_AR.indexOf(newCategory);
		if (newCategoryIndex !== categoryIndex) {
			setCategoryIndex(newCategoryIndex);
		}
	}, [categoryIndex]);

	// Handle case where we want to load the DG&A tab from the video overlay back button on mobile.
	const { videoOpenedFrom } = videoStore.stateSnapshot;
	if (videoOpenedFrom === 'mobileVSOverlay') {
		setCategoryIndex(1);
		videoStore.setGlobalState({ videoOpenedFrom: null });
	}

	const overlayData = categoryIndex === 0 ? allVitals : allVideos;

	const navToVital = useCallback((value, group) => {
		closeOverlay();

		const navigated = get('route').navigate({
			vitalsign: value,
			altid: group || 0,
			animating: 'f',
			start: '',
			end: '',
			__remove: ['spacecraft', 'altView', 'videoId']
		}, 'vitalsign');

		// In case of no change, router won't navigate
		// Force reset time rate for `satellites`
		if (!navigated && value === 'satellites') {
			get('time').setRealTime();
		}
	}, [closeOverlay, get]);

	const navToVideo = useCallback(value => {
		closeOverlay();
		videoStore.setGlobalState({ videoOpenedFrom: 'mobileVSOverlay' });
		get('route').navigate({
			animating: 'f',
			start: '',
			end: '',
			videoId: get('video').getVideoIdFromTitle(value),
			__remove: ['spacecraft', 'altView', 'videoId', 'showOnGlobe']
		}, 'vitalsign');
	}, [closeOverlay, get]);

	const navFunction = categoryIndex === 0 ? navToVital : navToVideo;

	const nested = categoryIndex === 1;

	// Nest videos with titles under respective categories
	const nestedCategories = {
		'الرسوم المتحركة العالمية': ['الرسوم المتحركة العالمية', 'فيديو جغرافي'],
		'فيديو, مخططات ورسوم بيانية': ['فيديو','رسم بياني']
	};

	// compose overlay
  let filteredOverlayData = overlayData.filter((v) => {return !v.hideFromOverlay})
  // console.log("overlayData", overlayData, filteredOverlayData)

	return (
		<Overlay type='vitals' ref={mainRef}>
			<Overlay.Header
				back
				onClose={closeOverlay}
				isMobile={isMobile}
				title={TITLE_TEXT}
				Icon={LayerGroupSolidSVG}
			>
				<Filter
					type='categories'
					allItems={Category_AR}
					selectedIndex={categoryIndex}
					updateItem={updateCategory}
				/>
			</Overlay.Header>
      
			<div className='content-mask'>
				{nested && isMobile
					? (
						<div className='content flex-grid-scrollable'>
							{Object.keys(nestedCategories).map(category => (
								<div key={category}>
									<NestedVitalRow
										title={category}
										categories={nestedCategories}
										isMobile={isMobile}
										overlayData={filteredOverlayData.filter(dataset => nestedCategories[category].includes(dataset.title))}
										nested={nested}
										category={category}
										onItemClick={navFunction}
										scrollableRef={mainRef}
									/>
								</div>
							))}
						</div>
					) :
						<GridList
							rowClassName={isMobile ? 'vital-mobile-row' : 'flex-grid-row'}
							maxPerRow={isMobile ? 1 : 1000}
							dataList={filteredOverlayData}
							itemSize={300}
							itemPadding={[30, 30]}
							itemComponent={<VitalItem isMobile={isMobile} onItemClick={navFunction} />}
							scrollableRef={isWindows ? scrollableNodeRef : mainRef}
						/>}
			</div>
		</Overlay>
	);

	// compose overlay
	return (
		<Overlay type='vitals' ref={mainRef}>
			{isWindows ?
				<SimpleBar scrollableNodeProps={{ ref: scrollableNodeRef }} style={{ height: '100%' }}>
					{overlayComponents}
				</SimpleBar> :
				overlayComponents}
		</Overlay>
	);
};

export default VitalsOverlay;
